import { debounce } from '@/lib/utils';
import ProductGrid from '@/islands/product-grid';

class FacetForm extends HTMLElement {
  private static filterData: { html: string; url: string }[] = [];
  private static searchParamsInitial = window.location.search.slice(1);
  private static searchParamsPrev = window.location.search.slice(1);
  private static hasPopstateListener = false;
  private static lastFocusedLabelFor: string | null = null;
  private static hasGlobalClearHandler = false;

  constructor() {
    super();
    this.initializeEventListeners();
    if (!FacetForm.hasPopstateListener) {
      window.addEventListener('popstate', () => {
        const searchParams = window.location.search.slice(1);
        this.renderPage(searchParams, null, false);
      });
      FacetForm.hasPopstateListener = true;
    }

    // Attach a single delegated click handler to support the "Clear filters" button
    // in the collection empty state (outside of the facet form/drawer markup)
    if (!FacetForm.hasGlobalClearHandler) {
      document.addEventListener('click', (event) => {
        const target = event.target as Element | null;
        if (!target) return;
        const button = target.closest('button');
        if (!button) return;

        // Ignore buttons inside the facet drawer; those are handled by type="reset"
        if (button.closest('facet-drawer')) return;

        // Detect the empty-state clear button inside the product grid container by
        // checking for a count badge within the button.
        const inGridContainer = !!button.closest('#product-grid-container');
        const hasActiveCount = !!button.querySelector('[data-active-filter-count]');

        if (inGridContainer && hasActiveCount) {
          const anyFacetForm = document.querySelector('facet-form') as FacetForm | null;
          if (anyFacetForm && typeof anyFacetForm.resetAll === 'function') {
            anyFacetForm.resetAll(event as unknown as Event);
          }
        }
      });
      FacetForm.hasGlobalClearHandler = true;
    }
  }

  public initializeEventListeners = () => {
    const facetForm = this.querySelector('form');

    // Remove existing listeners to avoid duplicates
    facetForm?.removeEventListener('input', this.debouncedOnSubmit);
    facetForm?.removeEventListener('submit', this.onSubmitHandler);

    // Add event listeners
    facetForm?.addEventListener('input', this.debouncedOnSubmit);
    facetForm?.addEventListener('submit', this.onSubmitHandler);

    // Add reset button listener
    const resetButton = this.querySelector('[type="reset"]');
    resetButton?.removeEventListener('click', this.resetAll);
    resetButton?.addEventListener('click', this.resetAll);

    // Add keyboard support for labels
    this.addKeyboardSupport();
  };

  private addKeyboardSupport = () => {
    const labels = this.querySelectorAll('label[tabindex="0"]');
    labels.forEach((label) => {
      label.addEventListener('keydown', (event) => {
        const keyboardEvent = event as KeyboardEvent;
        if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
          event.preventDefault();
          const input = document.getElementById(label.getAttribute('for') || '');
          if (input) {
            (input as HTMLInputElement).click();
          }
        }
      });
    });
  };

  debouncedOnSubmit = debounce((event: Event) => {
    this.onSubmitHandler(event);
  }, 800);

  onSubmitHandler = (event: Event) => {
    event.preventDefault();
    const target = event.target as HTMLElement;
    const form = target.closest('form');
    if (!form) return;
    // Capture currently focused chip label within facet drawer to restore focus after re-render
    const activeElement = (document.activeElement as HTMLElement) || null;
    if (
      activeElement &&
      activeElement.tagName === 'LABEL' &&
      activeElement.hasAttribute('for') &&
      !!activeElement.closest('facet-drawer')
    ) {
      FacetForm.lastFocusedLabelFor = activeElement.getAttribute('for');
    } else {
      FacetForm.lastFocusedLabelFor = null;
    }
    const searchParams = this.createSearchParams(form);
    // Sync aria-pressed on quick filter labels for SR state before rendering
    this.syncQuickFilterToggleState();
    // Force history-style render so quick filter also re-renders
    this.renderPage(searchParams, null);
  };

  onSubmitForm = (searchParams: string, event: Event) => {
    this.renderPage(searchParams, event);
  };

  createSearchParams = (_form: HTMLFormElement) => {
    // Get all forms on the page to combine their data
    const allForms = document.querySelectorAll('facet-form form');
    const combinedParams = new URLSearchParams();

    allForms.forEach((formElement) => {
      const formData = new FormData(formElement as HTMLFormElement);
      for (const [key, value] of formData.entries()) {
        const stringValue = value.toString();

        // Skip empty values and placeholder values for price range inputs
        if (stringValue.trim() === '') continue;

        // Skip price range inputs that match their placeholder values
        const input = formElement.querySelector(`[name="${key}"]`) as HTMLInputElement;
        if (
          input &&
          input.type === 'number' &&
          input.placeholder &&
          (key === 'filter.v.price.gte' || key === 'filter.v.price.lte')
        ) {
          // Build a locale-safe placeholder number
          const rawPlaceholder = input.placeholder.trim();
          const cleaned = rawPlaceholder.replace(/[^0-9.,]/g, '');
          let placeholderValue = cleaned;
          if (cleaned.includes(',') && cleaned.includes('.')) {
            if (cleaned.lastIndexOf('.') > cleaned.lastIndexOf(',')) {
              placeholderValue = cleaned.replace(/,/g, '');
            } else {
              placeholderValue = cleaned.replace(/\./g, '').replace(',', '.');
            }
          } else if (cleaned.includes(',')) {
            placeholderValue = cleaned.replace(',', '.');
          } else {
            placeholderValue = cleaned;
          }

          if (stringValue === placeholderValue || stringValue === '') continue;

          // Heuristic: treat near-boundary integer values as equal to actual boundary to avoid redundant filters
          const valueNum = parseFloat(stringValue);
          const isMax = key === 'filter.v.price.lte';
          const isMin = key === 'filter.v.price.gte';

          if (!Number.isNaN(valueNum)) {
            // Prefer numeric data attributes for accurate boundaries
            const dataMaxAttr = input.getAttribute('data-max');
            const dataMinAttr = input.getAttribute('data-min');
            const placeholderNum = parseFloat(placeholderValue);
            const parsedMaxAttr = parseFloat(dataMaxAttr || '');
            const parsedMinAttr = parseFloat(dataMinAttr || '');
            const maxBoundary = isMax ? (Number.isFinite(parsedMaxAttr) ? parsedMaxAttr : placeholderNum) : null;
            const minBoundary = isMin ? (Number.isFinite(parsedMinAttr) ? parsedMinAttr : placeholderNum) : null;

            if (isMax && typeof maxBoundary === 'number' && Number.isFinite(maxBoundary)) {
              // Example: boundary 511.2, submitted 511 -> floor(511.2) === 511 => skip
              if (Math.floor(maxBoundary) === valueNum) continue;
            }
            if (isMin && typeof minBoundary === 'number' && Number.isFinite(minBoundary)) {
              // Example: boundary 199.2, submitted 200 -> ceil(199.2) === 200 => skip
              if (Math.ceil(minBoundary) === valueNum) continue;
            }
          }
        }

        combinedParams.append(key, stringValue);
      }
    });

    // Preserve search query and options[prefix] if they exist in URL
    const currentParams = new URLSearchParams(window.location.search);
    if (!combinedParams.has('q') && currentParams.has('q')) {
      combinedParams.set('q', currentParams.get('q') || '');
    }
    if (!combinedParams.has('options[prefix]') && currentParams.has('options[prefix]')) {
      combinedParams.set('options[prefix]', currentParams.get('options[prefix]') || 'last');
    }

    // If setting is enabled, enforce availability=1 only when any user filter is present
    const forceAvailability = this.closest('facet-form')?.getAttribute('data-force-availability') === 'true';
    if (forceAvailability) {
      const hasAnyUserFilter = Array.from(combinedParams.keys()).some((key) => {
        // Any filter.* except the availability flag itself counts
        if (key.startsWith('filter.') && key !== 'filter.v.availability') return true;
        return false;
      });

      if (hasAnyUserFilter) {
        combinedParams.set('filter.v.availability', '1');
      } else {
        combinedParams.delete('filter.v.availability');
      }
    }

    return combinedParams.toString();
  };

  private renderPage = (searchParams: string, event: Event | null, updateURLHash = true) => {
    // Set all data-loading="false" to data-loading="true"
    const dataLoadingFalse = document.querySelectorAll('[data-loading="false"]');
    dataLoadingFalse.forEach((element) => {
      element.setAttribute('data-loading', 'true');
    });

    FacetForm.searchParamsPrev = searchParams;
    const sections = this.getSections();

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element: { url: string }) => element.url === url;

      if (FacetForm.filterData.some(filterDataUrl)) {
        this.renderSectionFromCache(filterDataUrl, event);
      } else {
        this.renderSectionFromFetch(url, event);
      }
    });

    if (updateURLHash) this.updateURLHash(searchParams);
  };

  private static reinitializeAllFacetForms = () => {
    const allFacetForms = document.querySelectorAll('facet-form');
    allFacetForms.forEach((facetForm) => {
      const facetFormElement = facetForm as FacetForm;
      if (facetFormElement.initializeEventListeners) {
        facetFormElement.initializeEventListeners();
      }
    });
  };

  private renderSectionFromFetch = (url: string, event: Event | null) => {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetForm.filterData = [...FacetForm.filterData, { html, url }];
        const isQuickFilterEvent = !!(
          event && document.getElementById('facet-quick-filter')?.contains(event.target as Node)
        );
        const isHistoryNav = event === null;
        if (!isQuickFilterEvent || isHistoryNav) {
          this.renderFilters(html, event);
        }
        this.renderProductGridContainer(html);
        this.renderProductCount(html);
        if (isQuickFilterEvent || isHistoryNav) {
          this.renderQuickFilter(html);
        }

        // Ensure all facet forms are properly initialized after update
        FacetForm.reinitializeAllFacetForms();
      });
  };

  private renderSectionFromCache = (filterDataUrl: (element: { url: string }) => boolean, event: Event | null) => {
    const html = FacetForm.filterData.find(filterDataUrl)?.html;
    if (!html) return;
    const isQuickFilterEvent = !!(
      event && document.getElementById('facet-quick-filter')?.contains(event.target as Node)
    );
    const isHistoryNav = event === null;
    if (!isQuickFilterEvent || isHistoryNav) {
      this.renderFilters(html, event);
    }
    this.renderProductGridContainer(html);
    this.renderProductCount(html);
    if (isQuickFilterEvent || isHistoryNav) {
      this.renderQuickFilter(html);
    }

    // Ensure all facet forms are properly initialized after update
    FacetForm.reinitializeAllFacetForms();
  };

  private renderProductGridContainer = (html: string) => {
    const container = document.getElementById('product-grid-container');

    if (!container) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newContainer = parsed.getElementById('product-grid-container');

    // Replace inner HTML
    container.innerHTML = newContainer?.innerHTML || '';

    // Sync pagination dataset attributes so ProductGrid has correct state
    if (newContainer) {
      const currentPage = newContainer.getAttribute('data-current-page') || '1';
      const totalPages = newContainer.getAttribute('data-total-pages') || '1';
      const hasNextPage = newContainer.getAttribute('data-has-next-page') || '0';

      container.setAttribute('data-current-page', currentPage);
      container.setAttribute('data-total-pages', totalPages);
      container.setAttribute('data-has-next-page', hasNextPage);
    }

    // Reinitialize the ProductGrid component to restore load more functionality
    const productGrid = document.getElementById('product-grid') as ProductGrid;
    if (productGrid) {
      // Reset pagination data and reinitialize
      productGrid.initializePaginationData();
      productGrid.setupLoadMoreButton();
      productGrid.resetProductCardsLayout();

      // Remove loading state after reinitialization
      requestAnimationFrame(() => {
        productGrid.removeAttribute('data-loading');
      });
    }
  };

  private renderProductCount = (html: string) => {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const container = document.getElementById('product-count-container');

    if (container) {
      container.innerHTML = parsedHTML.getElementById('product-count-container')?.innerHTML || '';
      container.setAttribute('data-loading', 'false');
    }

    const dataCount =
      parsedHTML.querySelector('[data-active-filter-count]')?.getAttribute('data-active-filter-count') || '0';

    document.querySelectorAll('[data-active-filter-count]').forEach((element) => {
      element.setAttribute('data-active-filter-count', dataCount);
      element.innerHTML = dataCount;
    });

    const filterClearBtn = document.getElementById('filters-clear-btn');
    if (filterClearBtn) {
      filterClearBtn.innerHTML = parsedHTML.getElementById('filters-clear-btn')?.innerHTML || '';
      if (dataCount && dataCount !== '0') {
        filterClearBtn.removeAttribute('disabled');
      } else {
        filterClearBtn.setAttribute('disabled', 'true');
      }
    }

    // Announce updated product count and filter count to screen readers
    const liveRegion = document.getElementById('product-updates-live-region');
    const countText = parsedHTML.getElementById('product-count-container')?.querySelector('span')?.textContent?.trim();
    const filterCount = dataCount;
    if (liveRegion) {
      let announcement = '';
      if (countText && countText.length > 0) {
        announcement += countText;
      }
      if (filterCount && filterCount !== '0') {
        announcement += (announcement ? '. ' : '') + `${filterCount} filters applied`;
      } else {
        announcement += (announcement ? '. ' : '') + `No filters applied`;
      }
      // Toggle text to ensure announcement fires even if same string
      liveRegion.textContent = '';
      liveRegion.textContent = announcement;
    }
  };

  private renderQuickFilter = (html: string) => {
    const container = document.getElementById('facet-quick-filter');
    if (!container) return;

    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const newQuickFilter = parsedHTML.getElementById('facet-quick-filter');

    if (newQuickFilter) {
      const focused = document.activeElement;
      const focusedFor = focused?.getAttribute('for');
      container.innerHTML = newQuickFilter.innerHTML;

      // Refocus for keyboard control continuity
      if (focusedFor) {
        (this.querySelector(`[for='${focusedFor}']`) as HTMLElement)?.focus();
      }
      // Event listeners will be reinitialized by reinitializeAllFacetForms()
      this.syncQuickFilterToggleState();
    }
  };

  private renderFilters = (html: string, event: Event | null) => {
    const focused = document.activeElement;
    const focusedId = focused?.getAttribute('id');
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll('facet-drawer details[id^="details-"]');
    const facetDetailsElementsFromDom = document.querySelectorAll('facet-drawer details[id^="details-"]');

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element: Element) => {
      const details = event?.target instanceof Element ? event.target.closest('details') : undefined;
      return details ? element.id === details.id : false;
    };

    const fetchDetailsArray = Array.from(facetDetailsElementsFromFetch);
    const facetsToRender = fetchDetailsArray.filter((element) => !matchesId(element));
    const countsToRender = fetchDetailsArray.find(matchesId);

    facetsToRender.forEach((elementToRender) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        // Update contents; preserve existing open/aria-expanded state
        (currentElement as HTMLElement).innerHTML = elementToRender.innerHTML;
        return;
      }

      // Insert new facet in the correct order relative to fetched list
      const liveContainer =
        document.querySelector('facet-drawer details[id^="details-"]')?.parentElement ||
        document.querySelector('facet-drawer .overflow-y-scroll') ||
        null;
      if (!liveContainer) return;

      const indexInFetch = fetchDetailsArray.findIndex((el) => el.id === elementToRender.id);
      let inserted = false;
      for (let i = indexInFetch + 1; i < fetchDetailsArray.length; i += 1) {
        const nextId = fetchDetailsArray[i].id;
        const nextLive = document.getElementById(nextId);
        if (nextLive && nextLive.parentElement === liveContainer) {
          liveContainer.insertBefore(elementToRender, nextLive);
          inserted = true;
          break;
        }
      }
      if (!inserted) {
        liveContainer.appendChild(elementToRender);
      }
    });

    if (countsToRender && event?.target instanceof Element) {
      const closestDetails = event.target.closest('details');
      if (closestDetails) {
        const newFacetDetailsElement = document.getElementById(closestDetails.id);
        if (newFacetDetailsElement) {
          const summary = newFacetDetailsElement.querySelector('summary');
          if (summary) {
            // Only move focus to the summary for text/number inputs (e.g., price range)
            const inputType = event.target.getAttribute('type');
            const shouldFocusSummary =
              !FacetForm.lastFocusedLabelFor && (inputType === 'text' || inputType === 'number');
            if (shouldFocusSummary) (summary as HTMLElement).focus();
          }
        }
      }
    }

    // Restore focus to the previously focused chip label inside the drawer, if any
    if (FacetForm.lastFocusedLabelFor) {
      const labelToFocus = document.querySelector(
        `facet-drawer label[for="${FacetForm.lastFocusedLabelFor}"]`,
      ) as HTMLElement | null;
      if (labelToFocus) labelToFocus.focus();
      FacetForm.lastFocusedLabelFor = null;
    } else if (focusedId) {
      // Restore focus by ID
      (this.querySelector(`[id='${focusedId}']`) as HTMLElement)?.focus();
    }
  };

  private updateURLHash = (searchParams: string) => {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  };

  private getSections = () => {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return [];
    return [
      {
        section: productGrid.dataset.id,
      },
    ];
  };

  resetAll = (event: Event) => {
    event.preventDefault();

    // Reset all facet forms on the page
    const allForms = document.querySelectorAll('facet-form form');
    allForms.forEach((form) => {
      (form as HTMLFormElement).reset();
      // Reset all checkbox inputs
      form.querySelectorAll('input[type="checkbox"]').forEach((element) => {
        (element as HTMLInputElement).setAttribute('aria-checked', 'false');
        (element as HTMLInputElement).removeAttribute('checked');
      });
      // Reset all number inputs
      form.querySelectorAll('input[type="number"]').forEach((element) => {
        (element as HTMLInputElement).value = '';
      });
    });

    // Build minimal params so we reload without filters or sorting.
    // Preserve only search query context for search pages.
    const currentParams = new URLSearchParams(window.location.search);
    const minimalParams = new URLSearchParams();
    if (currentParams.has('q')) {
      minimalParams.set('q', currentParams.get('q') || '');
    }
    if (currentParams.has('options[prefix]')) {
      minimalParams.set('options[prefix]', currentParams.get('options[prefix]') || 'last');
    }
    const searchParams = minimalParams.toString();
    this.syncQuickFilterToggleState();
    // Clear cache to avoid stale quick filter state and force full re-render
    FacetForm.filterData = [];
    // Force history-style render so quick filter re-renders with full options
    this.renderPage(searchParams, null);
  };

  private syncQuickFilterToggleState = () => {
    const quickFilter = document.getElementById('facet-quick-filter');
    if (!quickFilter) return;
    quickFilter.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      const input = checkbox as HTMLInputElement;
      const label = quickFilter.querySelector(`label[for="${input.id}"]`);
      if (label) {
        label.setAttribute('aria-pressed', input.checked ? 'true' : 'false');
      }
    });
  };
}

if (!customElements.get('facet-form')) {
  customElements.define('facet-form', FacetForm);
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange);
      element.addEventListener('keydown', this.onKeyDown);
    });
    this.setMinAndMaxValues();
  }

  onRangeChange = (event: Event) => {
    this.adjustToValidValues(event.currentTarget as HTMLInputElement);
    this.setMinAndMaxValues();
  };

  onKeyDown = (event: KeyboardEvent) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  setMinAndMaxValues = () => {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput?.getAttribute('data-max') || '');
  };

  adjustToValidValues = (input: HTMLInputElement) => {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  };
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange);
}
